import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, ArrowLeft, FileSpreadsheet } from 'lucide-react';
import { api, fmt, type Row } from '../../lib/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useMe } from '../../lib/useMe';
import { cleanPolicyId } from '../../lib/format';

// Per-carrier landing for the agent. Cards: total earned, # policies, paid vs pending.
// Click → see all policies attributed to them under that carrier.

export default function MyCarriers() {
  const { agentId } = useMe();

  const q = useQuery({
    queryKey: ['agent-rows', agentId],
    queryFn: () => api.agentRows(agentId),
    enabled: !!agentId,
  });

  if (q.isLoading) return <LoadingSpinner label="Loading your earnings…" />;
  if (q.isError) return <div className="alert alert-error"><p>{(q.error as Error).message}</p></div>;

  const rows = q.data?.rows ?? [];

  // Group by sys + carrier
  const groups = useMemoGroups(rows);

  if (rows.length === 0) {
    return (
      <div className="space-y-4 animate-fadeIn">
        <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
          My Carriers
        </h2>
        <div className="card text-center py-12">
          <Building2
            size={32}
            className="mx-auto"
            style={{ color: 'var(--color-text-muted)' }}
          />
          <p className="mt-3" style={{ color: 'var(--color-text-muted)' }}>
            No commission policies attributed to you yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
          My Carriers
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Click a carrier to see every policy attributed to you under it. Net is your share after
          any upline override.
        </p>
      </div>

      {Object.entries(groups).map(([sys, carrierGroups]) => (
        <div key={sys} className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
            {sys}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {carrierGroups.map((g) => (
              <Link
                key={`${sys}-${g.carrier}`}
                to={`/my/carriers/${encodeURIComponent(sys)}/${encodeURIComponent(g.carrier)}`}
                className="card card-hover"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3
                    className="text-base font-semibold"
                    style={{ color: 'var(--color-text)' }}
                  >
                    {g.carrier}
                  </h3>
                  <Building2 size={18} style={{ color: 'var(--color-primary)' }} />
                </div>
                <p
                  className="text-2xl font-bold font-mono mb-2"
                  style={{ color: 'var(--color-text)' }}
                >
                  {fmt.money(g.net)}
                </p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {g.rows} {g.rows === 1 ? 'policy' : 'policies'} ·{' '}
                  <span style={{ color: 'var(--color-primary-text)' }}>
                    {g.paidRows} paid
                  </span>
                  {g.unpaidRows > 0 && (
                    <span className="text-amber-500"> · {g.unpaidRows} pending</span>
                  )}
                </p>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function MyCarrierDetail() {
  const { sys, carrier } = useParams<{ sys: string; carrier: string }>();
  const { agentId } = useMe();
  const [filter, setFilter] = useState<'all' | 'paid' | 'pending'>('all');

  const q = useQuery({
    queryKey: ['agent-rows', agentId],
    queryFn: () => api.agentRows(agentId),
    enabled: !!agentId,
  });

  if (q.isLoading) return <LoadingSpinner label="Loading…" />;
  if (q.isError) return <div className="alert alert-error"><p>{(q.error as Error).message}</p></div>;
  if (!sys || !carrier) return <div className="alert alert-warning"><p>Bad URL</p></div>;

  const sysName = decodeURIComponent(sys);
  const carrierName = decodeURIComponent(carrier);
  const allRows = (q.data?.rows ?? []).filter(
    (r) => r.sys === sysName && r.carrier === carrierName,
  );
  const filtered = allRows.filter((r) =>
    filter === 'all' ? true : filter === 'paid' ? r.paid : !r.paid,
  );

  const totals = filtered.reduce(
    (acc, r) => ({
      raw: acc.raw + (r.raw_amount || 0),
      net: acc.net + (r.net_amount || 0),
    }),
    { raw: 0, net: 0 },
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-3">
        <Link to="/my/carriers" className="btn-icon" style={{ color: 'var(--color-text-muted)' }}>
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
            {carrierName}
          </h2>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
            {sysName}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Metric label="Policies" value={filtered.length.toString()} />
        <Metric label="Raw total" value={fmt.money(totals.raw)} />
        <Metric label="Net to you" value={fmt.money(totals.net)} accent />
      </div>

      <div className="flex gap-2">
        {(['all', 'paid', 'pending'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'} text-sm py-1 px-3`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden p-0">
        <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            <FileSpreadsheet size={14} className="inline mr-2" />
            Policies
          </h3>
        </div>
        {filtered.length === 0 ? (
          <p className="p-6 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
            No policies in this filter.
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Policy</th>
                <th>Policyholder</th>
                <th className="text-right">Commission</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={`${r.batch_id}-${r.row_idx}`}>
                  <td className="font-mono text-xs">
                    {cleanPolicyId(r.policy_id, r.writing_agent_id)}
                  </td>
                  <td>{r.policyholder_name}</td>
                  <td className="text-right font-mono font-semibold">
                    {fmt.money(r.net_amount)}
                  </td>
                  <td>
                    {r.paid ? (
                      <span className="badge badge-success text-xs">Paid</span>
                    ) : (
                      <span className="badge badge-warning text-xs">Pending</span>
                    )}
                  </td>
                  <td className="text-xs">{fmt.dateOnly(r.paid_to_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`metric-card ${accent ? 'metric-card-success' : 'metric-card-primary'}`}>
      <p
        className="text-xs uppercase tracking-wider mb-1"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {label}
      </p>
      <p className="text-2xl font-bold font-mono" style={{ color: 'var(--color-text)' }}>
        {value}
      </p>
    </div>
  );
}

function useMemoGroups(rows: Row[]) {
  return useMemo(() => {
    const out: Record<
      string,
      { carrier: string; rows: number; paidRows: number; unpaidRows: number; net: number }[]
    > = {};
    const map = new Map<string, Map<string, { rows: number; paidRows: number; unpaidRows: number; net: number }>>();
    for (const r of rows) {
      const sys = r.sys;
      const c = r.carrier || '?';
      if (!map.has(sys)) map.set(sys, new Map());
      const inner = map.get(sys)!;
      if (!inner.has(c)) inner.set(c, { rows: 0, paidRows: 0, unpaidRows: 0, net: 0 });
      const cell = inner.get(c)!;
      cell.rows++;
      if (r.paid) cell.paidRows++;
      else cell.unpaidRows++;
      cell.net += r.net_amount || 0;
    }
    for (const [sys, inner] of map.entries()) {
      out[sys] = [...inner.entries()]
        .map(([c, v]) => ({
          carrier: c,
          ...v,
          net: Math.round((v.net + Number.EPSILON) * 100) / 100,
        }))
        .sort((a, b) => b.net - a.net);
    }
    return out;
  }, [rows]);
}
